import { useInfiniteQuery } from '@tanstack/react-query';
import * as chatApi from '../../api/chat';
import type { ChatMessage } from '../../types/chat';
import { chatKeys } from './chatKeys';

const PAGE_SIZE = 50;

/**
 * Paginated message history for a room.
 *
 * The server returns messages in DESC order when `before` is supplied (the
 * default direction for "scroll up to load history"). We flatten pages and
 * keep them in the cache as `pages[0]` = most recent page (chronologically
 * latest), with each subsequent fetched page being older. The UI sorts on
 * render so callers get a stable ASC array via `flatMessages`.
 */
export function useChatMessages(roomId: string | null | undefined) {
  const query = useInfiniteQuery({
    queryKey: roomId ? chatKeys.messages(roomId) : ['chat', 'messages', 'disabled'],
    enabled: !!roomId,
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      return chatApi.listMessages(roomId!, {
        before: pageParam,
        limit: PAGE_SIZE,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: ChatMessage[]) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      const oldest = lastPage[lastPage.length - 1];
      return oldest?.id;
    },
    staleTime: 30_000,
  });

  // Flat ASC list for rendering. Server returns DESC; reverse and dedupe.
  const flatMessages: ChatMessage[] = (() => {
    if (!query.data) return [];
    const seen = new Set<string>();
    const out: ChatMessage[] = [];
    for (const page of query.data.pages) {
      for (const msg of page) {
        if (seen.has(msg.id)) continue;
        seen.add(msg.id);
        out.push(msg);
      }
    }
    return out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  })();

  return { ...query, flatMessages };
}
